// Canonical wire shape for BookedEventInfo.references on the purchase APIs:
// an ARRAY of EventBookingReference objects — { refType, refId, subIds,
// customMap }. The backend (in.aumhum.aumhum.bean.entity.event
// .EventBookingReference) rejects/coerces anything else, and historically
// callers have passed a single object, a bare context object (the clinic
// walk-in patient info), or [undefined] (the de-addiction funnel when no form
// data exists). Every payAndMakeEventBooking caller funnels through
// normalizeBookingReferences so the request body always carries the canonical
// shape regardless of what the caller had on hand.

const CANONICAL_KEYS = ['refType', 'refId', 'subIds', 'customMap']

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function hasContent(value) {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (isPlainObject(value)) return Object.keys(value).length > 0
  return true
}

function toCanonicalReference(entry) {
  if (entry === null || entry === undefined) return null
  if (typeof entry === 'string') {
    const trimmed = entry.trim()
    return trimmed ? { refId: trimmed } : null
  }
  if (!isPlainObject(entry)) return null

  if (CANONICAL_KEYS.some((key) => key in entry)) {
    const reference = {}
    CANONICAL_KEYS.forEach((key) => {
      if (hasContent(entry[key])) reference[key] = entry[key]
    })
    return Object.keys(reference).length > 0 ? reference : null
  }

  // Arbitrary context object from a legacy caller — preserve it without
  // guessing semantics so nothing is silently dropped.
  return hasContent(entry) ? { customMap: entry } : null
}

/**
 * Normalizes whatever a caller passed as reference data into the canonical
 * EventBookingReference[] the purchase APIs expect, or null when there is
 * nothing meaningful to send (matching the old `referenceData || null`).
 */
export function normalizeBookingReferences(referenceData) {
  const entries = Array.isArray(referenceData) ? referenceData : [referenceData]
  const references = entries.map(toCanonicalReference).filter(Boolean)
  return references.length > 0 ? references : null
}

/**
 * Shape-tolerant patient-id lookup across every reference shape callers have
 * used: the legacy flat context object, canonical references carrying patient
 * info in customMap, or a patient-profile reference whose refId is the id.
 */
export function extractPatientIdFromBookingReferences(referenceData) {
  const entries = Array.isArray(referenceData) ? referenceData : [referenceData]
  for (const entry of entries) {
    if (!isPlainObject(entry)) continue
    const candidate =
      entry.patientId ||
      entry.patientProfileId ||
      entry.customMap?.patientId ||
      entry.customMap?.patientProfileId ||
      (entry.refType === 'patient-profile' ? entry.refId : null)
    if (candidate) return candidate
  }
  return null
}
